import {
    Alert,
    ColorIndicator,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    Text,
    useAuth,
    useMerchantConfig,
    useModal,
    usePagination,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQCmpEventProductsQuery,
    useCmpEventByExternalIdQuery,
    useCmpEventProductDeleteMutation,
    useCmpEventProductsQuery,
} from "graphql-sdk";
import { formatAmount, formatDateTime } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback } from "react";
import { RootStackScreenProps } from "../../navigation";
import { useAdminSession } from "../../session";
import { AdminContainer } from "../container";
import { CmpEventProductCalendar } from "./cmp-event-product-calendar";
import { useHandleMutationError } from "../../hooks";

type ScreenProps = RootStackScreenProps<"CMP_EVENT">;

export function CmpEventProductListScreen({ route }: ScreenProps) {
    const auth = useAuth();
    auth.enforce(["merchant.cmp"]);

    const id = decodeURI(route.params.id);

    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const merchantConfig = useMerchantConfig();
    const theme = useTheme();
    const [t] = useTranslation();
    const { render } = useModal();

    const [cmpEventProductDelete] = useCmpEventProductDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "ASC",
    });

    const { data, loading, error, refetch } = useCmpEventProductsQuery({
        variables: {
            externalEventId: id,
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
        },
        fetchPolicy: "no-cache",
    });

    const { data: eventData, loading: eventLoading } =
        useCmpEventByExternalIdQuery({
            variables: {
                externalId: id,
            },
            fetchPolicy: "no-cache",
        });

    const handleCalendarPress = useCallback(
        async item => {
            await render(onClose => (
                <CmpEventProductCalendar
                    onClose={onClose}
                    output={item.calendar}
                />
            ));
        },
        [render]
    );

    const handleDelete = useCallback(
        async (productId: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.cmp_event_product.delete",
                            "Delete CMP event product?"
                        )}
                        bodyText={t(
                            "backoffice.cmp_event_product.delete_explain",
                            "If you choose to delete this CMP event product, the product will be deleted and removed from any layouts using the product.\n\nAre you sure, you want to delete this?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await cmpEventProductDelete({
                        variables: {
                            id: productId,
                        },
                    }),
                t("backoffice.cmp_product.deleted", "CMP product deleted"),
                async () => {
                    await refetch();
                }
            );
        },
        [handleMutationError, cmpEventProductDelete, refetch, render, t]
    );

    // Translations for print:
    // t("backoffice.cmp_event_product.print_always", "Always");
    // t("backoffice.cmp_event_product.print_never", "Never");
    // t("backoffice.cmp_event_product.print_next_day", "Next day");

    const renderCmpEventProductRow = useCallback(
        (
            item: GQCmpEventProductsQuery["cmpEventProducts"]["data"][0],
            rowIndex
        ) => (
            <DataTable.Row
                style={rowIndex % 2 ? theme.styles.rowIndex : undefined}
                key={`cmpEventId_${item.id}`}
            >
                <DataTable.Cell style={styles.iconColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{item.externalId}</DataTable.Cell>
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell numeric>
                    {item.amount === null
                        ? "-"
                        : formatAmount(item.amount, merchantConfig.currency, {
                              locale: locale as AvailableLocale,
                          })}
                </DataTable.Cell>

                <DataTable.Cell style={styles.vatColumn}>
                    <Text>{item.group.vat.percentage} %</Text>
                </DataTable.Cell>
                <DataTable.Cell>
                    {t(
                        "backoffice.cmp_event_product.print_" +
                            item.shouldPrint.toLowerCase(),
                        item.shouldPrint
                    )}
                </DataTable.Cell>
                <DataTable.Cell onPress={() => handleCalendarPress(item)}>
                    {item.calendar && (
                        <>
                            <Text numberOfLines={1}>{item.calendar}</Text>
                            <Icon name="eye" style={styles.icon} />
                        </>
                    )}
                </DataTable.Cell>
                <DataTable.Cell style={styles.centerColumn}>
                    {item.upgradeable
                        ? t("common.yes", "Yes")
                        : t("common.no", "No")}
                </DataTable.Cell>
                <DataTable.Cell style={styles.centerColumn}>
                    {item.sms ? t("common.yes", "Yes") : t("common.no", "No")}
                </DataTable.Cell>
                <DataTable.Cell style={styles.sequenceColumn} numeric>
                    {item.sequence}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.updatedAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell numeric style={styles.iconColumn}>
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                        testID={"delete:" + item.name}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            handleCalendarPress,
            handleDelete,
            locale,
            merchantConfig.currency,
            styles.centerColumn,
            styles.icon,
            styles.iconColumn,
            styles.sequenceColumn,
            styles.vatColumn,
            t,
            theme.colors.secondary,
            theme.styles.rowIndex,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    if (eventLoading) {
        return <Loading />;
    }

    return (
        <AdminContainer>
            <PageTitle>
                {t(
                    "backoffice.cmp_event_product.cmp_event",
                    "Event: {{name}}",
                    {
                        name: eventData?.cmpEventByExternalId?.name,
                    }
                )}
            </PageTitle>
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={styles.iconColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "externalId"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("externalId")}
                        >
                            {t("backoffice.common.external_id", "External ID")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title numeric>
                            {t("common.amount", "Amount")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.vatColumn}>
                            {t("common.vat", "Vat")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t("backoffice.cmp_event_product.print", "Print")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.cmp_event_product.calendar",
                                "Calendar"
                            )}
                        </DataTable.Title>
                        <DataTable.Title style={styles.centerColumn}>
                            {t(
                                "backoffice.cmp_event_product.upgradeable",
                                "Upgradeable"
                            )}
                        </DataTable.Title>
                        <DataTable.Title style={styles.centerColumn}>
                            {t("common.sms", "SMS")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.sequenceColumn} numeric>
                            {t("backoffice.common.sequence", "Sequence")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "updatedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("updatedAt")}
                        >
                            {t("common.updated_at", "Updated at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} numeric>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.cmpEventProducts.data.length === 0 ? (
                        <DataTable.Row style={styles.darkRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.cmp_event_product.no_data",
                                    "No products for event."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.cmpEventProducts.data.map(
                            renderCmpEventProductRow
                        )
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.cmpEventProducts.pagination.pages}
                        itemCount={
                            data?.cmpEventProducts.pagination.resultCount
                        }
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    darkRow: {
        backgroundColor: theme.colors.grey50,
    },
    icon: {
        color: theme.colors.secondary,
        paddingHorizontal: theme.spacingScale / 2,
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 60 },
    sequenceColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 80 },
    vatColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 80 },
    centerColumn: {
        justifyContent: "center",
    },
});
